import { useEffect, useRef, useState } from "react";
import { Pencil } from "lucide-react";

import { Textarea } from "@monorepo-apps/ui/src/components/textarea";

interface EvidenceFieldProps {
  value: string | null;
  vibesRevisit: boolean;
  placeholder?: string;
  onSave: (next: { evidence: string | null; vibesRevisit: boolean }) => void;
  onAcceptVibes?: () => void;
}

export function EvidenceField({
  value,
  vibesRevisit,
  placeholder,
  onSave,
  onAcceptVibes,
}: EvidenceFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const startEdit = () => {
    setDraft(value ?? "");
    setEditing(true);
  };

  useEffect(() => {
    if (editing) {
      textareaRef.current?.focus();
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    const next = trimmed.length > 0 ? trimmed : null;
    if (next === (value ?? null)) return;
    onSave({ evidence: next, vibesRevisit: next === null });
  };

  if (editing) {
    return (
      <Textarea
        ref={textareaRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        rows={2}
        placeholder={placeholder ?? "Link or quote that backs this score…"}
        className="text-[12.5px]"
      />
    );
  }

  if (value && value.length > 0) {
    return (
      <div className="flex flex-col items-start gap-1">
        {vibesRevisit ? (
          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-medium tracking-[0.04em]"
              style={{
                background: "oklch(0.93 0.10 80)",
                color: "oklch(0.45 0.16 80)",
              }}
              title="Score recorded without firm evidence — revisit"
            >
              <span
                className="inline-block size-1.5 rounded-full"
                style={{ background: "oklch(0.70 0.16 80)" }}
              />
              vibes
            </span>
            {onAcceptVibes ? (
              <button
                type="button"
                onClick={onAcceptVibes}
                className="text-foreground/50 hover:text-foreground text-[10.5px] underline-offset-2 hover:underline"
              >
                accept
              </button>
            ) : null}
          </div>
        ) : null}
        <button
          type="button"
          onClick={() => startEdit()}
          className="text-foreground/80 hover:text-foreground group inline-flex w-full items-start gap-1.5 rounded-md px-1 py-0.5 text-left text-[12.5px] leading-relaxed transition-colors"
        >
          <Pencil className="size-3 shrink-0 translate-y-[3px] opacity-0 transition-opacity group-hover:opacity-60" />
          <span className="line-clamp-2">{value}</span>
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => startEdit()}
        className="text-foreground/50 hover:text-foreground inline-flex items-center gap-1 text-[12px] italic transition-colors"
      >
        + add evidence
      </button>
      {vibesRevisit ? (
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-medium tracking-[0.04em]"
          style={{
            background: "oklch(0.93 0.10 80)",
            color: "oklch(0.45 0.16 80)",
          }}
          title="Score recorded without evidence — revisit"
        >
          <span
            className="inline-block size-1.5 rounded-full"
            style={{ background: "oklch(0.70 0.16 80)" }}
          />
          vibes
        </span>
      ) : null}
      {vibesRevisit && onAcceptVibes ? (
        <button
          type="button"
          onClick={onAcceptVibes}
          className="text-foreground/50 hover:text-foreground text-[10.5px] underline-offset-2 hover:underline"
        >
          accept
        </button>
      ) : null}
    </div>
  );
}
